import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from '../utils/logger';

export const useSubscriptionGuard = () => {
  const { user } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkSubscriptionStatus = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/api/subscription/status');
      if (response.data.success) {
        setSubscriptionData(response.data.data);
      }
    } catch (error) {
      logger.error('Error checking subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSubscriptionStatus();

    // Listen for subscription updates
    const handleSubscriptionUpdate = () => {
      checkSubscriptionStatus();
    };

    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    };
  }, [user]);

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    if (!subscriptionData?.status) return false;
    return subscriptionData.status.has_subscription === true;
  };

  // Check if user is on trial
  const isOnTrial = () => {
    if (!subscriptionData?.status) return false;
    return subscriptionData.status.status === 'trial';
  };

  // Check if trial is expired
  const isTrialExpired = () => {
    if (!subscriptionData?.status) return false;
    return subscriptionData.status.trial_expired === true;
  };

  // Check if subscription is cancelled
  const isSubscriptionCancelled = () => {
    if (!subscriptionData?.status) return false;
    return subscriptionData.status.status === 'cancelled';
  };

  // Check if cancelled subscription is still active
  const isCancelledButActive = () => {
    if (!subscriptionData?.status) return false;
    return subscriptionData.status.is_cancelled_but_active === true;
  };

  // Check if user needs subscription to continue
  const needsSubscription = () => {
    if (!subscriptionData?.status) return true;
    return subscriptionData.status.needs_subscription === true;
  };

  // Get trial days remaining
  const getTrialDaysRemaining = () => {
    if (!subscriptionData?.status) return 0;
    return subscriptionData.status.days_remaining || 0;
  };

  // Get status message
  const getStatusMessage = () => {
    if (!subscriptionData?.status) return 'No subscription';
    
    const status = subscriptionData.status;
    if (status.status === 'active') return 'Active Subscription';
    if (status.status === 'trial') return `Trial (${status.days_remaining} days left)`;
    if (status.status === 'cancelled' && status.is_cancelled_but_active) {
      return `Cancelled (expires in ${status.days_remaining} days)`;
    }
    if (status.status === 'cancelled') return 'Subscription Cancelled';
    if (status.status === 'expired') return 'Subscription Expired';
    return 'No subscription';
  };

  // Guard function that checks access - returns true if access should be allowed
  const guardFeature = (featureName = 'this feature') => {
    return !needsSubscription();
  };

  return {
    subscriptionData,
    loading,
    hasActiveSubscription,
    isOnTrial,
    isTrialExpired,
    isSubscriptionCancelled,
    isCancelledButActive,
    needsSubscription,
    getTrialDaysRemaining,
    getStatusMessage,
    guardFeature,
    refreshStatus: checkSubscriptionStatus
  };
}; 